import { actionChannel, all, call, fork, put, race, select, take, takeLatest } from 'redux-saga/effects';
import { buffers, delay } from 'redux-saga';
import type { Action } from 'redux';
import type { Channel } from 'redux-saga';
import {
    type CallEndEventPayload,
    type CallEventMeta,
    type CallStartedPayload,
    callEnded,
    callStarted,
    changeCallMode,
    startCallDetection,
    stopCallDetection,
    types as callTypes,
    updateCallTimer
} from '../../actions/backendCall';
import { types as commonTypes } from '../../actions/backendCommon';
import { forwardEvent, types as mainTypes } from '../../actions/backendMain';
import { CallEvents, CallMode, EXPLICIT_INTEGRATION_SOURCE, MAX_CALL_DETECTION_SESSION_DURATION } from '../../../constants/backend';
import { proto } from '../../../proto/AsrProto';
import { getCallId, getMetaCallId, getSessionDurationMsec, isCallStarted } from '../../selectors/call';
import { getDefaultCallMode, getExplicitIntegrationSource } from '../../selectors/config';
import { IS_LEGACY_LISTENER_MODE } from '../../../config/electron';
import { getMetaInfo } from '../../../utils/background/general';
import { captureInfoMessage, captureWarnMessage } from '../../../tracker/raven';
import { forwardingLogger } from '../../../utils/logger/loggerFactory';
import { types as asrTypes } from '../../actions/backendAsr';
import { types as deviceTypes } from '../../actions/backendDevice';

type SagaAction = {
    type?: string;
    payload?: Record<string, unknown>;
} & Action<string>;

interface CallEndedInput {
    sessionOffsetMsec: number;
    reason: unknown;
    eventMeta: CallEventMeta;
}

function toCallEndedInput(value: unknown): CallEndedInput {
    if (typeof value !== 'object' || value === null) {
        return {
            sessionOffsetMsec: 0,
            reason: proto.FinalizationReason.CALL_END_NORMAL,
            eventMeta: {}
        };
    }

    const candidate = value as {
        sessionOffsetMsec?: unknown;
        reason?: unknown;
        eventMeta?: unknown;
    };

    return {
        sessionOffsetMsec: typeof candidate.sessionOffsetMsec === 'number' ? candidate.sessionOffsetMsec : 0,
        reason: candidate.reason ?? proto.FinalizationReason.CALL_END_NORMAL,
        eventMeta: (typeof candidate.eventMeta === 'object' && candidate.eventMeta !== null ? candidate.eventMeta : {}) as CallEventMeta
    };
}

function toCallStartedPayload(payload: Record<string, unknown> | undefined): CallStartedPayload {
    return {
        sessionStartOffsetMsec: typeof payload?.sessionStartOffsetMsec === 'number' ? payload.sessionStartOffsetMsec : undefined,
        id: typeof payload?.id === 'string' ? payload.id : payload?.id === null ? null : undefined,
        metaCallID: typeof payload?.metaCallID === 'string' ? payload.metaCallID : payload?.metaCallID === null ? null : undefined,
        eventMeta: (typeof payload?.eventMeta === 'object' && payload.eventMeta !== null ? payload.eventMeta : undefined) as
            | CallEventMeta
            | undefined,
        systemSoundRecognition: typeof payload?.systemSoundRecognition === 'boolean' ? payload.systemSoundRecognition : undefined,
        callId: typeof payload?.callId === 'string' || typeof payload?.callId === 'number' ? payload.callId : null,
        meta: typeof payload?.meta === 'object' && payload.meta !== null ? (payload.meta as Record<string, unknown>) : undefined
    };
}

function toCallMode(value: unknown, fallback = CallMode.DEFAULT): number {
    return typeof value === 'number' ? value : fallback;
}

export function* callTimer(): Generator<unknown, void, unknown> {
    while (true) {
        const { ended } = (yield race({
            ended: take(callTypes.CALL_ENDED),
            tick: call(delay, 1000)
        })) as { ended?: unknown };

        if (ended) {
            break;
        }
        yield put(updateCallTimer());
    }
}

export function* onCallStartReceived(action: SagaAction): Generator<unknown, void, unknown> {
    const isStarted = yield select(isCallStarted);
    if (!isStarted) {
        yield put(callStarted(toCallStartedPayload(action.payload)));
    } else {
        captureWarnMessage('call start received while call is already started', {
            source: 'backendCall.onCallStartReceived',
            payload: action.payload || null
        });
    }
}

export function* callStartReceived(): Generator<unknown, void, unknown> {
    // Buffer CALL_START_RECEIVED events so we don't lose one that arrives
    // before CALL_DETECTION_STARTED unblocks this saga. Without the channel,
    // a telephony START event that comes in while the ASR WebSocket is still
    // connecting is dispatched into the void — the takeLatest isn't registered
    // yet — and the call is silently dropped (isOnCall stays false, END is
    // skipped as "already ended"). The channel queues the event; takeLatest
    // drains it once detection is ready.
    const startChannel = (yield actionChannel(callTypes.CALL_START_RECEIVED)) as Channel<Action>;
    yield all([take(callTypes.CALL_DETECTION_STARTED)]);
    yield takeLatest(startChannel, onCallStartReceived);
}

export function* initCallTimer(): Generator<unknown, void, unknown> {
    yield takeLatest(callTypes.CALL_STARTED, callTimer);
}

export function* initCallDetection(): Generator<unknown, void, unknown> {
    const list = [take(commonTypes.ALL_SETTINGS_RECEIVED)];
    if (IS_LEGACY_LISTENER_MODE) {
        list.push(take(mainTypes.DAEMON_INITIALIZED));
        list.push(take(deviceTypes.RECORDING_INFO_RECEIVED));
    }
    yield all(list);
    yield put(startCallDetection());
}

export function* callDetectionMaxDurationTimeout(): Generator<unknown, void, unknown> {
    yield take(callTypes.CALL_DETECTION_STARTED);
    yield call(delay, MAX_CALL_DETECTION_SESSION_DURATION);
    yield put(stopCallDetection(proto.FinalizationReason.CALL_END_TIMEOUT));
}

export function* hold(): Generator<unknown, void, unknown> {
    while (true) {
        yield put({ type: callTypes.ASR_PING });
        const { unhold } = (yield race({
            unhold: take(callTypes.CALL_UNHOLDED),
            tick: call(delay, 5000)
        })) as { unhold?: unknown };
        if (unhold) {
            return;
        }
    }
}

export function* initOnHoldPing(): Generator<unknown, void, unknown> {
    yield takeLatest(callTypes.CALL_HOLDED, hold);
}

export function* onCallEndReceived(action: SagaAction): Generator<unknown, void, unknown> {
    // Guard: if the call was already force-ended by the isEventFromNewCall branch
    // (which happens when a new START arrives before callEndReceived can run),
    // isCallStarted is already false and we must not dispatch a second CALL_ENDED.
    // A double CALL_ENDED kills the freshly-started detection session for the new call.
    const isStarted = (yield select(isCallStarted)) as boolean;
    if (!isStarted) {
        captureInfoMessage('skipping call end because call is already ended', {
            source: 'backendCall.onCallEndReceived',
            payload: action.payload || null
        });
        return;
    }
    const { callEndEvent } = (action.payload || {}) as { callEndEvent?: CallEndEventPayload };
    yield put(callEnded(toCallEndedInput(callEndEvent)));
}

export function* callEndReceived(): Generator<unknown, void, unknown> {
    yield takeLatest(callTypes.CALL_END_RECEIVED, onCallEndReceived);
}

/**
 * Non-blocking helper: waits for RECOGNITION_READY and then applies the call mode.
 * If the call ends before recognition is ready, the mode change is skipped.
 * This replaces the blocking `yield take(RECOGNITION_READY)` that previously
 * caused start events to be lost when an END arrived during the wait.
 */
export function* applyCallModeWhenReady(
    mode: number,
    forgetAudioAndTranscript: boolean,
    onEvent: string | null,
    useDefaultCallMode = false
): Generator<unknown, void, unknown> {
    const { ready } = (yield race({
        ready: take(asrTypes.RECOGNITION_READY),
        ended: take(callTypes.CALL_ENDED)
    })) as { ready?: unknown; ended?: unknown };

    if (ready) {
        if (useDefaultCallMode) {
            const defaultCallMode = (yield select(getDefaultCallMode)) as number;
            yield put(changeCallMode(mode, null, null, defaultCallMode));
        } else {
            yield put(changeCallMode(mode, forgetAudioAndTranscript, onEvent));
        }
    }
    // If ended won the race, we simply exit — the call mode change is no longer relevant.
}

export function* onCallEventReceived(action: SagaAction): Generator<unknown, void, unknown> {
    const { callEvent, eventBody, callMode, forgetAudioAndTranscript } = action.payload || {};
    const eventBodyRecord = (eventBody || {}) as Record<string, unknown>;
    const eventTimestamp = eventBodyRecord.timestamp;

    const isOnCall = yield select(isCallStarted);
    const currentCallID = yield select(getCallId);
    const currentMetaCallID = yield select(getMetaCallId);
    const callMeta = getMetaInfo(eventBodyRecord);
    const metaCallID = callMeta.call_id || null;
    const explicitIntegrationSource = yield select(getExplicitIntegrationSource);
    const isEventFromNewCall = isOnCall && (!currentMetaCallID || currentMetaCallID !== metaCallID);
    const callEventContext = {
        source: 'backendCall.onCallEventReceived',
        callEvent,
        currentCallID,
        currentMetaCallID,
        incomingMetaCallID: metaCallID,
        isOnCall,
        isEventFromNewCall,
        explicitIntegrationSource,
        callMode: typeof callMode === 'number' ? callMode : null,
        forgetAudioAndTranscript: typeof forgetAudioAndTranscript === 'boolean' ? forgetAudioAndTranscript : null,
        eventTimestamp: typeof eventTimestamp === 'string' || typeof eventTimestamp === 'number' ? eventTimestamp : null
    };

    // Always preserve the raw external event before any local lifecycle handling.
    // These events are diagnostic breadcrumbs; they must not depend on whether
    // the local call state later applies, skips, or waits for ASR restart.
    yield put(
        forwardEvent({
            jsonRequest: eventBody
        })
    );

    // Note: the raw event body is already captured by `'internal call event raw'`
    // in ForwardingConnection.onCallEvent BEFORE parsing/validation. Here we log
    // only the saga-level processing context (current call ids, isOnCall, isEventFromNewCall,
    // explicit integration source, etc) so the two log lines complement each other
    // instead of duplicating the payload.
    captureInfoMessage('call state event received', {
        ...callEventContext,
        type: 'callEvent'
    });

    if (isEventFromNewCall && callEvent === CallEvents.START) {
        // we need to finish current call first
        captureWarnMessage('previous call is not ended', callEventContext);
        captureInfoMessage('forcing previous call end before applying new start', callEventContext);

        const sessionOffsetMsec = (yield select(getSessionDurationMsec)) as number;
        yield put(
            callEnded({
                reason: proto.FinalizationReason.CALL_END_NORMAL,
                sessionOffsetMsec,
                eventMeta: {
                    source: 'call_event'
                }
            })
        );

        // wait for new asr session
        yield all([take(callTypes.CALL_DETECTION_STARTED)]);
        captureInfoMessage('new call detection started after forced end', callEventContext);
    }

    forwardingLogger.log('callEvent:', callEvent);

    // For non-START events: if both call IDs are known and don't match, this event belongs
    // to a different call (e.g. a stale END arriving after a new call has already started).
    // forwardEvent above already recorded it on the server — just skip local dispatch.
    const isStaleNonStartEvent = callEvent !== CallEvents.START && !!metaCallID && !!currentMetaCallID && metaCallID !== currentMetaCallID;

    if (isStaleNonStartEvent) {
        captureInfoMessage('skipping stale call event', callEventContext);
        return;
    }

    switch (callEvent) {
        case CallEvents.START:
            if (!currentMetaCallID || currentMetaCallID !== metaCallID) {
                yield put({
                    type: callTypes.CALL_START,
                    payload: {
                        meta: callMeta,
                        eventMeta: {
                            source: 'call_event'
                        }
                    }
                });
                // Non-blocking: apply call mode once recognition is ready.
                // Previously `yield take(RECOGNITION_READY)` blocked the entire saga,
                // so an END event arriving before RECOGNITION_READY caused the call
                // start to be lost (the ASR connection was destroyed by the END handler
                // while this saga was still waiting).
                yield fork(applyCallModeWhenReady, toCallMode(callMode), Boolean(forgetAudioAndTranscript), callTypes.CALL_START);
            } else {
                captureInfoMessage('skipping duplicate start for current call', callEventContext);
            }
            break;
        case CallEvents.END:
            if (callMode) {
                yield put(changeCallMode(toCallMode(callMode), Boolean(forgetAudioAndTranscript), CallEvents.END));
            }
            yield put({
                type: callTypes.CALL_END,
                payload: {
                    eventMeta: {
                        source: 'call_event'
                    }
                }
            });
            break;
        case CallEvents.CHANGE:
            if (callMode) {
                yield put(changeCallMode(toCallMode(callMode), Boolean(forgetAudioAndTranscript), CallEvents.CHANGE));
            }
            break;
        case CallEvents.HOLD:
            yield put({ type: callTypes.CALL_HOLD });
            break;
        case CallEvents.UNHOLD:
            yield put({ type: callTypes.CALL_UNHOLD });
            break;
        case CallEvents.PING:
            yield put({ type: callTypes.UTC_PING });

            if (callMode && explicitIntegrationSource === EXPLICIT_INTEGRATION_SOURCE.UCT_CALL_EVENT) {
                yield put(changeCallMode(toCallMode(callMode), null, CallEvents.PING));
            }
            break;
        default:
            captureWarnMessage('unknown call event received', callEventContext);
    }
}

/**
 * Strictly serialized processing of CALL_EVENT_RECEIVED.
 *
 * Previously this used `takeEvery`, which is mostly-but-not-fully sequential:
 * each dispatched action spawns its own saga instance, and those instances
 * can interleave whenever one of them `yield`s on something async
 * (e.g. `yield take(RECOGNITION_READY)` or the `isEventFromNewCall` branch
 * that waits for `CALL_DETECTION_STARTED`). With Electron IPC delivering
 * `call_end(old)` and `call_start(new)` ~100 ms apart, the start-handler
 * could race past the end-handler and either get dropped or overwrite state
 * in the wrong order.
 *
 * `actionChannel` buffers every CALL_EVENT_RECEIVED in strict FIFO and we
 * process them one-at-a-time via `call(onCallEventReceived, action)`. The
 * next event is only taken after the previous handler has fully completed,
 * restoring the effective pre-TS-migration behaviour without relying on the
 * (formerly broken, now correct) 1500 ms delay inside `EventQueue`.
 *
 * BUFFER CHOICE — this is production-critical:
 *   redux-saga's default `actionChannel(pattern)` uses `buffers.fixed(10)`
 *   with `ON_OVERFLOW_THROW`. If `onCallEventReceived` is waiting on a
 *   multi-second step (e.g. `yield all([take(CALL_DETECTION_STARTED)])` in
 *   the `isEventFromNewCall` branch, which waits for ASR teardown + restart)
 *   and >10 CALL_EVENT_RECEIVED actions pile up behind it, the channel
 *   throws "Channel's Buffer overflow!" and this saga terminates — silently
 *   breaking all subsequent call-event handling until a full reload.
 *   We use `buffers.expanding(50)` so the queue grows on demand instead of
 *   throwing; realistic worst-case is low tens of events during a multi-
 *   second ASR restart, so expansion will be rare in practice.
 */
export function* callEventReceived(): Generator<unknown, void, unknown> {
    const channel = (yield actionChannel(callTypes.CALL_EVENT_RECEIVED, buffers.expanding(50))) as Channel<SagaAction>;
    while (true) {
        const action = (yield take(channel)) as SagaAction;
        yield call(onCallEventReceived, action);
    }
}

export function* onCallRestartReceived(action: SagaAction): Generator<unknown, void, unknown> {
    const isStarted = yield select(isCallStarted);
    if (isStarted) {
        const currentCallID = yield select(getCallId);
        const currentMetaCallID = yield select(getMetaCallId);
        const payloadMeta = (((action || {}).payload || {}).meta || {}) as Record<string, unknown>;
        const restartContext = {
            source: 'backendCall.onCallRestartReceived',
            currentCallID,
            currentMetaCallID,
            payloadMetaCallID: payloadMeta.call_id || null,
            eventMeta: action?.payload?.eventMeta || null
        };
        // we need to finish current call first
        captureWarnMessage('previous call is not ended', restartContext);
        captureInfoMessage('forcing previous call end before restart', restartContext);

        const sessionOffsetMsec = (yield select(getSessionDurationMsec)) as number;
        yield put(
            callEnded({
                reason: proto.FinalizationReason.CALL_END_NORMAL,
                sessionOffsetMsec,
                eventMeta: {
                    source: 'call_event'
                }
            })
        );

        // wait for new asr session
        yield all([take(callTypes.CALL_DETECTION_STARTED)]);
        captureInfoMessage('call detection started after forced restart', restartContext);
    }

    yield put({
        type: callTypes.CALL_START,
        payload: {
            meta: action?.payload?.meta || {},
            eventMeta: action?.payload?.eventMeta
        }
    });

    // Non-blocking: same fix as in onCallEventReceived.
    yield fork(applyCallModeWhenReady, CallMode.DEFAULT, false, null, true);
}

export function* callRestartReceived(): Generator<unknown, void, unknown> {
    yield takeLatest(callTypes.CALL_RESTART, onCallRestartReceived);
}

export default [
    callStartReceived(),
    initCallTimer(),
    initCallDetection(),
    callDetectionMaxDurationTimeout(),
    initOnHoldPing(),
    callEndReceived(),
    callEventReceived(),
    callRestartReceived()
];
